import Date from "../Inputs/Date";
import { Backdrop, Button, CircularProgress, Grid } from "@mui/material";
import { BudgetItem, BuildReportColumnLabels, CurrentUser, CustomReportFilter, CustomReportSettings, DateTime, FamilyBucketLabel, GetEnabledBucketDefinitions, Grouping, TransactionItem, USCurrency, BudgetTransaction } from "../../types";
import { useMemo, useState } from "react";
import ModalHeader from "./ModalHeader";
import ModalFooterCustom from "./ModalFooterCustom";
import { useSelector } from "react-redux";
import { useItems } from "../../features/Fetch/Items";
import { DownloadPdf } from "../../features/ReportService";
import ReportPreviewTables from "../Reports/ReportPreviewTables";
import { buildReportPreview } from "../../features/Reports/preview";

const BudgetReport = (props: any) => {
	let budget: BudgetItem = props.Budget;
	let transactions: TransactionItem[] = props.Transactions;
	let checkRequests: TransactionItem[] = props.CheckRequests;
	let user: CurrentUser = useSelector((state: any) => state.userData.value);
	let budgetLabel = FamilyBucketLabel(user, "budget", "Budget");

	const bucketColumnKeys = useMemo(() => GetEnabledBucketDefinitions(user)
		.filter(d => d.bucketTypeKey?.toLowerCase() !== 'budget')
		.map(d => `Bucket:${d.bucketTypeKey}`), [user]);
	const columns = useMemo(() => ["Date", "Name", "Merchant", ...bucketColumnKeys, "Description", "Amount"], [bucketColumnKeys]);
	const grouping = bucketColumnKeys[0] ?? "";
	const reportTitle = `Report By ${budgetLabel}`;

	let [startDate, setStartDate] = useState(props.StartDate as DateTime);
	let [endDate, setEndDate] = useState(props.EndDate as DateTime);
	let [overlay, setOverlay] = useState(false);

	let repo = useItems();

	const { groups: budgetGroups } = buildReportPreview({
		transactions: transactions || [],
		checkRequests: checkRequests || [],
		budgets: [budget],
		subBudgets: budget?.subBudgets ?? [],
		settings: {
			title: reportTitle,
			columns,
			grouping,
			filter: {
				startDate: startDate,
				endDate: endDate,
				budgetIds: [budget.budgetId],
				includeSpending: true,
				includeMiles: true,
				includeCheckRequests: true,
				combineSplitBudgets: false,
				includeReceipts: false,
				groupReceipts: false
			} as CustomReportFilter
		} as CustomReportSettings
	});

	return (<>
		<ModalHeader Title={budget.description} CanDelete={false} Close={props.Close} />
		<div className="modal-body mt-5">
			<Grid container spacing={1}>
				<Date Size={4} Label="From" Value={startDate} OnChange={(v: any) => setStartDate(new DateTime(v))} />
				<Date Size={4} Label="To" Value={endDate} OnChange={(v: any) => setEndDate(new DateTime(v))} />
				<Grid item xs={4} >
					<h1 className="report-total">{USCurrency(budgetGroups.reduce((total: number, g: Grouping) => g.Value.reduce((sub: number, b: BudgetTransaction) => b.Amount.amount + sub, 0) + total, 0))}</h1>
				</Grid>
			</Grid>

			<ReportPreviewTables
				groups={budgetGroups}
				columns={columns}
				grouping={grouping}
			/>
		</div>
		<ModalFooterCustom HideSave={!user.isHoH} ShowSave={true} SaveText={`Download Report`} Close={props.Close} Save={() => Export(budgetGroups)} />
		<Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={overlay}>
			<CircularProgress />
		</Backdrop>
	</>);


	function Export(items: Grouping[]) {
		setOverlay(true);
		setTimeout(() => GenerateReport(items), 50);
	}

	async function GenerateReport(_items: Grouping[]) {
		let settings: CustomReportSettings = {
			title: reportTitle,
			columns: columns,
			columnLabels: BuildReportColumnLabels(user, columns),
			grouping: grouping,
			filter: {
				startDate: startDate,
				endDate: endDate,
				budgetIds: [budget.budgetId],
				includeSpending: true,
				includeMiles: true,
				includeCheckRequests: true,
				combineSplitBudgets: false,
				includeReceipts: false,
				groupReceipts: false
			} as CustomReportFilter
		}
		let file = await repo.GetReportPdfAsync(settings);
		DownloadPdf(await file.blob() as Blob, reportTitle);
		setOverlay(false);
	}
}

export default BudgetReport;